import { useEffect, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { styles } from "./styles";
import NotificationsBell from "./components/NotificationsBell";
import HeaderMenu from "./components/HeaderMenu";
import { db, functions } from "./firebase";
import { doc, onSnapshot } from "firebase/firestore";
import useAuth from "./hooks/useAuth";
import useOrgData from "./hooks/useOrgData";
import useCapabilities from "./hooks/useCapabilities";
import LoginPage from "./auth/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import FacilityDashboard from "./pages/FacilityDashboard";
import FacilityBuildingsPage from "./pages/FacilityBuildingsPage";
import FacilityJobsPage from "./pages/FacilityJobsPage";
import SignedTicketsPage from "./pages/SignedTicketsPage";
import NotificationSettingsPage from "./pages/NotificationSettingsPage";
import MessagesPage from "./pages/MessagesPage";
import MessageThreadPage from "./pages/MessageThreadPage";
import FacilityQuotesPage from "./pages/FacilityQuotesPage";
import FacilityInvoicesPage from "./pages/FacilityInvoicesPage";
import ConnectionsPage from "./pages/ConnectionsPage";
import MyTimePage from "./pages/MyTimePage";
import PayrollPage from "./pages/PayrollPage";
import CompanySettingsPage from "./pages/CompanySettingsPage";
import EquipmentByStatusPage from "./pages/EquipmentByStatusPage";
import FacilityJobDetailPage from "./pages/FacilityJobDetailPage";
import FacilityServiceTicketsPage from "./pages/FacilityServiceTicketsPage";
import FacilityWorkRecordsPage from "./pages/FacilityWorkRecordsPage";
import BrandHeader from "./components/BrandHeader";
import ProfilePromptBanner from "./components/ProfilePromptBanner";
import ProfilePage from "./pages/ProfilePage";
import CustomerDetailPage from "./pages/CustomerDetailPage";
import LocationDetailPage from "./pages/LocationDetailPage";
import EquipmentDetailPage from "./pages/EquipmentDetailPage";
import WarrantiesPage from "./pages/WarrantiesPage";
import ContactsPage from "./pages/ContactsPage";
import DeletedItemsPage from "./pages/DeletedItemsPage";
import OwnerPanelPage from "./pages/OwnerPanelPage";
import ManageUsersPage from "./pages/ManageUsersPage";
import RecentActivityPage from "./pages/RecentActivityPage";
import JobsPage from "./pages/JobsPage";
import JobsPageMobile from "./pages/JobsPageMobile";
import JobDetailPage from "./pages/JobDetailPage";
import JobDetailPageMobile from "./pages/JobDetailPageMobile";
import ClosedBillsPage from "./pages/ClosedBillsPage";
import ClosedJobsPage from "./pages/ClosedJobsPage";
import { isNativeMobile } from "./utils/platform";
import ServiceTicketsPage from "./pages/ServiceTicketsPage";
import QuotesPage from "./pages/QuotesPage";
import QuoteEditor from "./pages/QuoteEditor";
import QuoteView from "./pages/QuoteView";
import InvoicesPage from "./pages/InvoicesPage";
import ContractsPage from "./pages/ContractsPage";
import VendorsPage from "./pages/VendorsPage";
import MarginReportPage from "./pages/MarginReportPage";
import ContractDetailPage from "./pages/ContractDetailPage";
import POsPage from "./pages/POsPage";
import {
  displayName,
  hasCapability,
  isAdminTier,
  visibleBuildings,
} from "./utils/capabilities";
import CustomersPage from "./pages/CustomersPage";
import PmListPage from "./pages/PmListPage";

export default function App() {
  const auth = useAuth();
  const { user, isOwner, orgId, role, orgType, authLoading, handleLogout } =
    auth;

  // Caller's own org name — fetched from organizations/{orgId} once we know
  // the orgId. Declared early so useOrgData can stamp it onto every write
  // (createdByOrgName) and the header can show it.
  const [orgName, setOrgName] = useState("");
  const [orgPayTypes, setOrgPayTypes] = useState(null);
  const [orgPayPeriodConfig, setOrgPayPeriodConfig] = useState(null);
  const [orgQuoteDefaults, setOrgQuoteDefaults] = useState(null);
  const [orgMaintenanceDefaults, setOrgMaintenanceDefaults] = useState(null);
  const [orgLogoUrl, setOrgLogoUrl] = useState("");
  const [orgAddress, setOrgAddress] = useState({});
  const [orgLocked, setOrgLocked] = useState(false);

  // useCapabilities runs first so we have the user's profile (display name)
  // to pass into useOrgData — writes will then stamp the name alongside the
  // email on records / messages.
  const {
    capabilities,
    department,
    notificationPreferences,
    profile,
    dismissedNotificationIds,
    threadLastRead,
  } = useCapabilities(user);

  const {
    customers,
    customersRaw,
    locationsRaw,
    contactsRaw,
    jobsRaw,
    serviceTicketsRaw,
    quotesRaw,
    purchaseOrdersRaw,
    contractsRaw,
    vendorsRaw,
    timeEntriesRaw,
    facilityServiceTicketsRaw,
    publicJobsRaw,
    publicTicketsRaw,
    publicInvoicesRaw,
    publicQuotesRaw,
    notificationsRaw,
    reconnectNotifications,
    messageThreadsRaw,
    reconnectMessageThreads,
    globalAuditLog,
    dataLoading,
    errorMessage,
    actions,
  } = useOrgData(user, orgId, role, profile, orgName);

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [warrantiesForEquipment, setWarrantiesForEquipment] = useState(null);
  const [selectedContactLocation, setSelectedContactLocation] = useState(null);
  const [selectedContactCustomer, setSelectedContactCustomer] = useState(null);
  const [showDeletedItemsPage, setShowDeletedItemsPage] = useState(false);
  const [showClosedBills, setShowClosedBills] = useState(false);
  const [showClosedJobs, setShowClosedJobs] = useState(false);
  const [showManageUsersPage, setShowManageUsersPage] = useState(false);
  // When an admin opens another employee's profile from Manage Employees,
  // we stash the target here so ProfilePage knows to edit that user's doc
  // instead of the signed-in user's own doc.
  const [profileEditTarget, setProfileEditTarget] = useState(null);
  const [showRecentActivityPage, setShowRecentActivityPage] = useState(false);
  const [showJobsPage, setShowJobsPage] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showServiceTicketsPage, setShowServiceTicketsPage] = useState(false);
  const [showQuotesPage, setShowQuotesPage] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showInvoicesPage, setShowInvoicesPage] = useState(false);
  const [showContractsPage, setShowContractsPage] = useState(false);
  const [showVendorsPage, setShowVendorsPage] = useState(false);
  const [showMarginPage, setShowMarginPage] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [quoteMode, setQuoteMode] = useState("view"); // "view" | "edit"
  const [showPOsPage, setShowPOsPage] = useState(false);
  const [showFacilityBuildings, setShowFacilityBuildings] = useState(false);
  const [showFacilityJobs, setShowFacilityJobs] = useState(false);
  const [selectedFacilityJob, setSelectedFacilityJob] = useState(null);
  const [showFacilityTickets, setShowFacilityTickets] = useState(false);
  const [showFacilityWorkRecords, setShowFacilityWorkRecords] = useState(false);
  const [showSignedTickets, setShowSignedTickets] = useState(false);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  const [selectedThread, setSelectedThread] = useState(null);
  const [showFacilityQuotes, setShowFacilityQuotes] = useState(false);
  const [showFacilityInvoices, setShowFacilityInvoices] = useState(false);
  const [showConnections, setShowConnections] = useState(false);
  const [showMyTime, setShowMyTime] = useState(false);
  const [showPayroll, setShowPayroll] = useState(false);
  const [showCompanySettings, setShowCompanySettings] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  // Caller's own org name — fetched from organizations/{orgId} once we know
  // the orgId. (Declared earlier in the file — see the useState above the
  // useOrgData call — so writes can stamp createdByOrgName.)
  // Optional initial filter when opening ServiceTicketsPage (e.g. straight
  // to the "Signed" archive view from the dashboard tile).
  const [serviceTicketsInitialStatus, setServiceTicketsInitialStatus] =
    useState("all");
  const [showCustomersPage, setShowCustomersPage] = useState(false);
  const [pmListView, setPmListView] = useState(null);
  // null | "yellow" | "red" — drill-in from Equipment Health tiles on dashboards.
  const [equipmentStatusView, setEquipmentStatusView] = useState(null);
  // Optional pre-filtered equipment list to show (contractor side scopes to
  // opted-in buildings; facility side shows all of theirs).
  const [equipmentStatusList, setEquipmentStatusList] = useState([]);

  const [bootstrapping, setBootstrapping] = useState(false);
  const [bootstrapMessage, setBootstrapMessage] = useState("");

  const handleBootstrap = async () => {
    setBootstrapping(true);
    setBootstrapMessage("");
    try {
      const callable = httpsCallable(functions, "bootstrapOwner");
      const result = await callable();
      setBootstrapMessage(
        result.data?.message ||
          "Owner claim set. Log out and back in to refresh your session."
      );
    } catch (err) {
      setBootstrapMessage(`Bootstrap failed: ${err.message}`);
    } finally {
      setBootstrapping(false);
    }
  };

  useEffect(() => {
    if (!user) {
      setSelectedCustomer(null);
      setSelectedLocation(null);
      setSelectedEquipment(null);
      setSelectedContactLocation(null);
      setSelectedContactCustomer(null);
      setShowDeletedItemsPage(false);
      setShowClosedBills(false);
      setShowClosedJobs(false);
      setShowManageUsersPage(false);
      setShowRecentActivityPage(false);
      setShowJobsPage(false);
      setSelectedJob(null);
      setShowServiceTicketsPage(false);
      setShowQuotesPage(false);
      setSelectedQuote(null);
      setShowInvoicesPage(false);
      setShowContractsPage(false);
      setSelectedContract(null);
      setShowPOsPage(false);
      setShowVendorsPage(false);
      setShowMarginPage(false);
      setShowFacilityBuildings(false);
      setShowFacilityJobs(false);
      setSelectedFacilityJob(null);
      setShowSignedTickets(false);
      setShowNotificationSettings(false);
      setShowMessages(false);
      setSelectedThread(null);
      setShowFacilityQuotes(false);
      setShowFacilityInvoices(false);
      setShowConnections(false);
      setShowMyTime(false);
      setShowPayroll(false);
      setShowCompanySettings(false);
      setShowProfile(false);
      setShowCustomersPage(false);
      setPmListView(null);
    }
  }, [user]);

  // Live listener on the org doc — covers the header name and the payroll
  // pay-types list. We use onSnapshot (not getDoc) so admin edits to
  // payTypes propagate without a refresh.
  useEffect(() => {
    if (!orgId) {
      setOrgName("");
      setOrgPayTypes(null);
      setOrgLogoUrl("");
      setOrgAddress({});
      setOrgLocked(false);
      return;
    }
    const unsub = onSnapshot(
      doc(db, `organizations/${orgId}`),
      (snap) => {
        const data = snap.exists() ? snap.data() : null;
        setOrgName(data?.name || "");
        setOrgPayTypes(
          Array.isArray(data?.payTypes) ? data.payTypes : null
        );
        setOrgPayPeriodConfig(
          data?.payPeriodConfig && typeof data.payPeriodConfig === "object"
            ? data.payPeriodConfig
            : null
        );
        setOrgQuoteDefaults(
          data?.quoteDefaults && typeof data.quoteDefaults === "object"
            ? data.quoteDefaults
            : null
        );
        setOrgMaintenanceDefaults(
          data?.maintenanceDefaults &&
            typeof data.maintenanceDefaults === "object"
            ? data.maintenanceDefaults
            : null
        );
        setOrgLogoUrl(data?.logoUrl || "");
        setOrgAddress(data?.address || {});
        setOrgLocked(!!data?.locked);
      },
      () => {
        setOrgName("");
        setOrgPayTypes(null);
        setOrgPayPeriodConfig(null);
        setOrgQuoteDefaults(null);
        setOrgMaintenanceDefaults(null);
        setOrgLogoUrl("");
        setOrgAddress({});
        setOrgLocked(false);
      }
    );
    return unsub;
  }, [orgId]);

  if (authLoading) return <div style={styles.page}>Loading...</div>;
  if (!user)
    return (
      <>
        <BrandHeader />
        <LoginPage auth={auth} />
      </>
    );

  if (isOwner) {
    return (
      <>
        <BrandHeader
          right={<button onClick={handleLogout}>Logout</button>}
        />
        <div style={styles.page}>
          <p style={{ fontSize: "12px", color: "#666" }}>
            Logged in as: {user.email}
          </p>
          <OwnerPanelPage />
        </div>
      </>
    );
  }

  if (dataLoading) return <div style={styles.page}>Loading...</div>;

  // Org-wide lockout. When the project owner toggles "Lock" on an org in
  // the Owner Console, every signed-in user in that org gets this screen
  // instead of the app — the only action available is logout. Project
  // owner bypasses this (they're routed to OwnerPanelPage above before
  // we get here) so they can unlock from the same panel.
  if (orgLocked) {
    return (
      <>
        <BrandHeader />
        <div style={{ ...styles.page, textAlign: "center", paddingTop: "60px" }}>
          <h2 style={{ color: "#9a6b00" }}>This organization is locked.</h2>
          <p style={{ fontSize: "14px", color: "#444", maxWidth: "440px", margin: "8px auto 16px" }}>
            Access has been temporarily disabled by the platform owner.
            Contact your administrator or the platform owner to restore
            access.
          </p>
          <button style={styles.button} onClick={handleLogout}>
            Log out
          </button>
        </div>
      </>
    );
  }

  const isOrgAdmin = isAdminTier(role);
  const isFacility = orgType === "facility";
  // Facility list views are filtered to the buildings this user may see
  // (executive = all; facilities_manager / maintenance = assigned).
  const facilityCustomers = isFacility
    ? customers.map((c) => ({
        ...c,
        locations: visibleBuildings(role, user.uid, c.locations || []),
      }))
    : customers;

  // Source currentCustomer from facilityCustomers on facility side so
  // CustomerDetailPage (and anything else rendering customer.locations)
  // only sees buildings the user is actually assigned to. Otherwise a
  // maintenance user could see all buildings on the back-from-detail page.
  const currentCustomer = (isFacility ? facilityCustomers : customers).find(
    (c) => c.id === selectedCustomer?.id
  );

  const isOnHome =
    !selectedCustomer &&
    !selectedLocation &&
    !selectedEquipment &&
    !selectedContactLocation &&
    !selectedContactCustomer &&
    !showDeletedItemsPage &&
    !showClosedBills &&
    !showClosedJobs &&
    !showManageUsersPage &&
    !showRecentActivityPage &&
    !showJobsPage &&
    !selectedJob &&
    !showServiceTicketsPage &&
    !showQuotesPage &&
    !showInvoicesPage &&
    !showContractsPage &&
    !showVendorsPage &&
    !showMarginPage &&
    !selectedContract &&
    !showPOsPage &&
    !showFacilityBuildings &&
    !showFacilityJobs &&
    !selectedFacilityJob &&
    !showFacilityTickets &&
    !showFacilityWorkRecords &&
    !showSignedTickets &&
    !showNotificationSettings &&
    !showMessages &&
    !selectedThread &&
    !showFacilityQuotes &&
    !showConnections &&
    !showMyTime &&
    !showPayroll &&
    !showCompanySettings &&
    !showProfile &&
    !showCustomersPage &&
    !pmListView &&
    !equipmentStatusView;

  const goHome = () => {
    setSelectedCustomer(null);
    setSelectedLocation(null);
    setSelectedEquipment(null);
    setSelectedContactLocation(null);
    setSelectedContactCustomer(null);
    setShowDeletedItemsPage(false);
    setShowClosedBills(false);
    setShowClosedJobs(false);
    setShowManageUsersPage(false);
    setShowRecentActivityPage(false);
    setShowJobsPage(false);
    setSelectedJob(null);
    setShowServiceTicketsPage(false);
    setShowQuotesPage(false);
    setSelectedQuote(null);
    setShowInvoicesPage(false);
    setShowContractsPage(false);
    setSelectedContract(null);
    setShowVendorsPage(false);
    setShowPOsPage(false);
    setShowFacilityBuildings(false);
    setShowFacilityJobs(false);
    setSelectedFacilityJob(null);
    setShowFacilityTickets(false);
    setShowFacilityWorkRecords(false);
    setShowSignedTickets(false);
    setShowNotificationSettings(false);
    setShowMessages(false);
    setSelectedThread(null);
    setShowFacilityQuotes(false);
    setShowFacilityInvoices(false);
    setShowConnections(false);
    setShowMyTime(false);
    setShowPayroll(false);
    setShowCompanySettings(false);
    setShowProfile(false);
    setProfileEditTarget(null);
    setShowCustomersPage(false);
    setPmListView(null);
    setEquipmentStatusView(null);
    setEquipmentStatusList([]);
  };

  // Notification deep-link router. Each notification carries a `link`
  // pointer (e.g. { type: "thread", threadId }) — clicking the alert calls
  // this with that pointer; we reset navigation and open the most relevant
  // page. The same notification type routes differently per orgType (e.g.
  // a job link goes to FacilityJobsPage for a facility, JobsPage for a
  // contractor).
  const navigateToNotification = (link) => {
    if (!link) return;
    goHome();
    switch (link.type) {
      case "thread": {
        // Mark the thread read for THIS user so the alert click also clears
        // the inbox highlight + the dashboard counter — same effect as
        // clicking the row in the inbox.
        if (link.threadId) actions.markThreadRead?.(link.threadId);
        const t = messageThreadsRaw.find((x) => x.id === link.threadId);
        if (t) setSelectedThread(t);
        else setShowMessages(true);
        break;
      }
      case "job": {
        if (isFacility) {
          setShowFacilityJobs(true);
        } else {
          const j = jobsRaw.find((x) => x.id === link.jobId);
          if (j) setSelectedJob(j);
          else setShowJobsPage(true);
        }
        break;
      }
      case "ticket": {
        if (isFacility) {
          setShowSignedTickets(true);
        } else {
          setServiceTicketsInitialStatus("signed");
          setShowServiceTicketsPage(true);
        }
        break;
      }
      case "invoice": {
        // Facility: land on the Invoices list — they want to see the bill
        // they were just notified about, not drill into the contractor's
        // job (which previously landed them on FacilityJobDetailPage where
        // Reopen/Start/Complete/File-ticket buttons all targeted the
        // contractor's org-private job doc they can't write to, producing
        // "No document to update" errors).
        // Contractor: open the job detail (where JobCosting + finalized
        // state lives).
        if (isFacility) {
          setShowFacilityInvoices(true);
        } else {
          const j = jobsRaw.find((x) => x.id === link.jobId);
          if (j) setSelectedJob(j);
          else setShowJobsPage(true);
        }
        break;
      }
      case "po": {
        setShowPOsPage(true);
        break;
      }
      case "quote": {
        if (isFacility) setShowFacilityQuotes(true);
        else setShowQuotesPage(true);
        break;
      }
      case "equipment":
      case "serviceRecord": {
        // Both link types target the same destination: the equipment
        // (where the service record is attached). When the notification
        // carries equipmentId + locationId we deep-link straight to
        // EquipmentDetailPage by setting the selected triple. Falls back
        // to the buildings / customers list when the lookup misses (legacy
        // notifications without equipmentId, equipment moved/deleted, etc).
        const tree = isFacility ? facilityCustomers : customers;
        let triple = null;
        if (link.equipmentId && link.locationId) {
          outer: for (const c of tree) {
            for (const l of c.locations || []) {
              if (l.id !== link.locationId) continue;
              for (const e of l.equipment || []) {
                if (e.id === link.equipmentId) {
                  triple = { customer: c, location: l, equipment: e };
                  break outer;
                }
              }
            }
          }
        }
        if (triple) {
          setSelectedCustomer(triple.customer);
          setSelectedLocation(triple.location);
          setSelectedEquipment(triple.equipment);
        } else if (isFacility) {
          setShowFacilityBuildings(true);
        } else {
          setShowCustomersPage(true);
        }
        break;
      }
      default:
        // Unknown link type — just leave them on the home screen we already
        // reset to.
        break;
    }
  };

  const renderPage = () => {
    if (showManageUsersPage && isOrgAdmin) {
      return (
        <ManageUsersPage
          orgId={orgId}
          orgType={orgType}
          currentUid={user.uid}
          actions={actions}
          orgName={orgName}
          dataCounts={{
            contracts: contractsRaw.length,
            jobs: jobsRaw.length,
            quotes: quotesRaw.length,
            purchaseOrders: purchaseOrdersRaw.length,
            vendors: vendorsRaw.length,
          }}
          onEditUserProfile={(u) => {
            setProfileEditTarget({
              uid: u.id,
              email: u.email,
              profile: u.profile || {},
            });
            setShowProfile(true);
            setShowManageUsersPage(false);
          }}
          onBack={() => setShowManageUsersPage(false)}
        />
      );
    }

    if (showFacilityWorkRecords) {
      return (
        <FacilityWorkRecordsPage
          jobsRaw={jobsRaw}
          facilityServiceTicketsRaw={facilityServiceTicketsRaw}
          customers={facilityCustomers}
          onSelectJob={(job) => {
            setShowFacilityWorkRecords(false);
            setSelectedFacilityJob(job);
          }}
          onBack={() => setShowFacilityWorkRecords(false)}
        />
      );
    }

    if (showFacilityTickets) {
      return (
        <FacilityServiceTicketsPage
          facilityServiceTicketsRaw={facilityServiceTicketsRaw}
          jobsRaw={jobsRaw}
          customers={facilityCustomers}
          actions={actions}
          user={user}
          role={role}
          onSelectJob={(job) => {
            setShowFacilityTickets(false);
            setSelectedFacilityJob(job);
          }}
          onBack={() => setShowFacilityTickets(false)}
        />
      );
    }

    if (selectedFacilityJob) {
      return (
        <FacilityJobDetailPage
          job={selectedFacilityJob}
          customers={facilityCustomers}
          facilityServiceTicketsRaw={facilityServiceTicketsRaw}
          publicInvoicesRaw={publicInvoicesRaw}
          actions={actions}
          user={user}
          role={role}
          orgId={orgId}
          onBack={() => setSelectedFacilityJob(null)}
        />
      );
    }

    if (showFacilityJobs) {
      return (
        <FacilityJobsPage
          jobsRaw={jobsRaw}
          publicJobsRaw={publicJobsRaw}
          publicTicketsRaw={publicTicketsRaw}
          publicInvoicesRaw={publicInvoicesRaw}
          customers={facilityCustomers}
          actions={actions}
          role={role}
          capabilities={capabilities}
          user={user}
          orgId={orgId}
          onSelectJob={(job) => setSelectedFacilityJob(job)}
          onBack={() => setShowFacilityJobs(false)}
        />
      );
    }

    if (showFacilityBuildings) {
      return (
        <FacilityBuildingsPage
          customers={facilityCustomers}
          role={role}
          capabilities={capabilities}
          actions={actions}
          orgId={orgId}
          onOpenBuilding={(account, building) => {
            setShowFacilityBuildings(false);
            setSelectedCustomer(account);
            setSelectedLocation(building);
          }}
          onBack={() => setShowFacilityBuildings(false)}
        />
      );
    }

    if (showRecentActivityPage) {
      return (
        <RecentActivityPage
          customers={isFacility ? facilityCustomers : customers}
          actions={actions}
          user={user}
          role={role}
          orgId={orgId}
          orgType={orgType}
          setSelectedCustomer={setSelectedCustomer}
          setSelectedLocation={setSelectedLocation}
          setSelectedEquipment={setSelectedEquipment}
          setSelectedContactLocation={setSelectedContactLocation}
          onBack={() => setShowRecentActivityPage(false)}
        />
      );
    }

    // Render JobDetailPage whenever a job is selected — independent of
    // showJobsPage. This lets Back fall through to whatever page set the
    // selection (InvoicesPage, JobsPage, etc.) so the user returns to
    // where they came from instead of always landing on JobsPage.
    if (selectedJob) {
      const liveJob = jobsRaw.find((j) => j.id === selectedJob.id);
      if (liveJob) {
        const JobDetailComponent = isNativeMobile()
          ? JobDetailPageMobile
          : JobDetailPage;
        return (
          <JobDetailComponent
            job={liveJob}
            customers={customers}
            jobsRaw={jobsRaw}
            serviceTicketsRaw={serviceTicketsRaw}
            purchaseOrdersRaw={purchaseOrdersRaw}
            errorMessage={errorMessage}
            actions={actions}
            role={role}
            capabilities={capabilities}
            user={user}
            profile={profile}
            orgId={orgId}
            logoUrl={orgLogoUrl}
            orgName={orgName}
            orgAddress={orgAddress}
            quoteDefaults={orgQuoteDefaults}
            maintenanceDefaults={orgMaintenanceDefaults}
            contractsRaw={contractsRaw}
            onBack={() => setSelectedJob(null)}
          />
        );
      }
    }

    if (showJobsPage) {
      const JobsComponent = isNativeMobile() ? JobsPageMobile : JobsPage;
      return (
        <JobsComponent
          customers={customers}
          jobsRaw={jobsRaw}
          actions={actions}
          role={role}
          capabilities={capabilities}
          user={user}
          setSelectedJob={setSelectedJob}
          onBack={() => setShowJobsPage(false)}
        />
      );
    }

    if (showQuotesPage) {
      const liveQuote = selectedQuote
        ? quotesRaw.find((q) => q.id === selectedQuote.id) || selectedQuote
        : null;
      if (liveQuote) {
        const qCustomer = customers.find((c) => c.id === liveQuote.customerId);
        const qLocation = qCustomer?.locations?.find(
          (l) => l.id === liveQuote.locationId
        );
        const canSeeMargin = hasCapability(role, capabilities, "editJobs");
        if (quoteMode === "edit") {
          return (
            <QuoteEditor
              quote={liveQuote}
              actions={actions}
              customer={qCustomer}
              customerName={qCustomer?.name}
              locationName={qLocation?.name}
              canSeeMargin={canSeeMargin}
              quoteDefaults={orgQuoteDefaults}
              orgId={orgId}
              onBack={() => setQuoteMode("view")}
            />
          );
        }
        return (
          <QuoteView
            quote={liveQuote}
            actions={actions}
            customer={qCustomer}
            location={qLocation}
            customerName={qCustomer?.name}
            locationName={qLocation?.name}
            jobsRaw={jobsRaw}
            canSeeMargin={canSeeMargin}
            canSend={
              hasCapability(role, capabilities, "createQuotes") ||
              role === "admin" ||
              role === "manager"
            }
            canMarkApproved={role === "admin" || role === "manager"}
            logoUrl={orgLogoUrl}
            orgName={orgName}
            orgAddress={orgAddress}
            quoteDefaults={orgQuoteDefaults}
            onEdit={() => setQuoteMode("edit")}
            onBack={() => setSelectedQuote(null)}
          />
        );
      }
      return (
        <QuotesPage
          quotesRaw={quotesRaw}
          customers={customers}
          actions={actions}
          role={role}
          capabilities={capabilities}
          user={user}
          quoteDefaults={orgQuoteDefaults}
          onOpenQuote={(q, mode = "view") => {
            setSelectedQuote(q);
            setQuoteMode(mode);
          }}
          onBack={() => setShowQuotesPage(false)}
        />
      );
    }

    if (showPOsPage) {
      return (
        <POsPage
          purchaseOrdersRaw={purchaseOrdersRaw}
          jobsRaw={jobsRaw}
          customers={customers}
          vendorsRaw={vendorsRaw}
          actions={actions}
          role={role}
          capabilities={capabilities}
          user={user}
          profile={profile}
          onShowVendors={
            hasCapability(role, capabilities, "manageVendors")
              ? () => {
                  setShowPOsPage(false);
                  setShowVendorsPage(true);
                }
              : undefined
          }
          onBack={() => setShowPOsPage(false)}
        />
      );
    }

    if (selectedContract) {
      const liveContract =
        contractsRaw.find((c) => c.id === selectedContract.id) ||
        selectedContract;
      return (
        <ContractDetailPage
          contract={liveContract}
          jobsRaw={jobsRaw}
          customers={customers}
          actions={actions}
          role={role}
          capabilities={capabilities}
          orgId={orgId}
          maintenanceDefaults={orgMaintenanceDefaults}
          onSelectJob={(job) => {
            setSelectedContract(null);
            setSelectedJob(job);
          }}
          onBack={() => setSelectedContract(null)}
        />
      );
    }

    if (showContractsPage) {
      return (
        <ContractsPage
          contractsRaw={contractsRaw}
          customers={customers}
          orgId={orgId}
          actions={actions}
          role={role}
          capabilities={capabilities}
          onOpenContract={(c) => setSelectedContract(c)}
          onBack={() => setShowContractsPage(false)}
        />
      );
    }

    if (showVendorsPage) {
      return (
        <VendorsPage
          vendorsRaw={vendorsRaw}
          actions={actions}
          role={role}
          capabilities={capabilities}
          onBack={() => setShowVendorsPage(false)}
        />
      );
    }

    if (showMarginPage) {
      return (
        <MarginReportPage
          jobsRaw={jobsRaw}
          customers={customers}
          role={role}
          capabilities={capabilities}
          onBack={() => setShowMarginPage(false)}
        />
      );
    }

    if (showInvoicesPage) {
      return (
        <InvoicesPage
          jobsRaw={jobsRaw}
          publicInvoicesRaw={publicInvoicesRaw}
          customers={customers}
          role={role}
          capabilities={capabilities}
          user={user}
          orgName={orgName}
          orgAddress={orgAddress}
          logoUrl={orgLogoUrl}
          actions={actions}
          onSelectJob={(job) => setSelectedJob(job)}
          onBack={() => setShowInvoicesPage(false)}
        />
      );
    }

    if (showServiceTicketsPage) {
      return (
        <ServiceTicketsPage
          serviceTicketsRaw={serviceTicketsRaw}
          customers={customers}
          jobsRaw={jobsRaw}
          actions={actions}
          role={role}
          capabilities={capabilities}
          user={user}
          initialStatus={serviceTicketsInitialStatus}
          logoUrl={orgLogoUrl}
          orgName={orgName}
          orgAddress={orgAddress}
          onBack={() => {
            setShowServiceTicketsPage(false);
            setServiceTicketsInitialStatus("all");
          }}
        />
      );
    }

    if (showSignedTickets) {
      return (
        <SignedTicketsPage
          publicTicketsRaw={publicTicketsRaw}
          customers={isFacility ? facilityCustomers : customers}
          logoUrl={orgLogoUrl}
          orgName={orgName}
          orgAddress={orgAddress}
          role={role}
          capabilities={capabilities}
          orgId={orgId}
          onBack={() => setShowSignedTickets(false)}
        />
      );
    }

    if (showNotificationSettings) {
      return (
        <NotificationSettingsPage
          user={user}
          role={role}
          notificationPreferences={notificationPreferences}
          onBack={() => setShowNotificationSettings(false)}
        />
      );
    }

    if (selectedThread) {
      return (
        <MessageThreadPage
          thread={selectedThread}
          actions={actions}
          orgId={orgId}
          user={user}
          role={role}
          onBack={() => setSelectedThread(null)}
        />
      );
    }

    if (showProfile) {
      return (
        <ProfilePage
          user={user}
          profile={profile}
          targetUser={profileEditTarget}
          onBack={() => {
            setShowProfile(false);
            // If we were admin-editing someone, bounce back to Manage Employees.
            if (profileEditTarget) {
              setProfileEditTarget(null);
              setShowManageUsersPage(true);
            }
          }}
        />
      );
    }

    if (showConnections) {
      return (
        <ConnectionsPage
          customersRaw={customersRaw}
          locationsRaw={locationsRaw}
          orgId={orgId}
          orgType={orgType}
          role={role}
          actions={actions}
          onBack={() => setShowConnections(false)}
        />
      );
    }

    if (showMyTime) {
      return (
        <MyTimePage
          user={user}
          actions={actions}
          customers={customers}
          timeEntriesRaw={timeEntriesRaw}
          jobsRaw={jobsRaw}
          payTypes={orgPayTypes}
          onBack={() => setShowMyTime(false)}
        />
      );
    }

    if (showPayroll) {
      return (
        <PayrollPage
          orgId={orgId}
          role={role}
          capabilities={capabilities}
          actions={actions}
          customers={customers}
          timeEntriesRaw={timeEntriesRaw}
          jobsRaw={jobsRaw}
          payTypes={orgPayTypes}
          payPeriodConfig={orgPayPeriodConfig}
          onBack={() => setShowPayroll(false)}
        />
      );
    }

    if (showCompanySettings) {
      return (
        <CompanySettingsPage
          orgName={orgName}
          logoUrl={orgLogoUrl}
          orgAddress={orgAddress}
          payPeriodConfig={orgPayPeriodConfig}
          quoteDefaults={orgQuoteDefaults}
          maintenanceDefaults={orgMaintenanceDefaults}
          actions={actions}
          onBack={() => setShowCompanySettings(false)}
        />
      );
    }

    if (showFacilityQuotes) {
      return (
        <FacilityQuotesPage
          publicQuotesRaw={publicQuotesRaw}
          customers={facilityCustomers}
          orgId={orgId}
          onBack={() => setShowFacilityQuotes(false)}
        />
      );
    }

    if (showFacilityInvoices) {
      return (
        <FacilityInvoicesPage
          publicInvoicesRaw={publicInvoicesRaw}
          customers={facilityCustomers}
          actions={actions}
          onBack={() => setShowFacilityInvoices(false)}
        />
      );
    }

    if (showMessages) {
      return (
        <MessagesPage
          messageThreadsRaw={messageThreadsRaw}
          customers={isFacility ? facilityCustomers : customers}
          jobsRaw={jobsRaw}
          orgId={orgId}
          user={user}
          threadLastRead={threadLastRead}
          actions={actions}
          onOpenThread={(t) => setSelectedThread(t)}
          onReload={reconnectMessageThreads}
          onBack={() => setShowMessages(false)}
        />
      );
    }

    if (showClosedBills) {
      return (
        <ClosedBillsPage
          jobsRaw={jobsRaw}
          customers={customers}
          role={role}
          capabilities={capabilities}
          actions={actions}
          user={user}
          profile={profile}
          logoUrl={orgLogoUrl}
          orgName={orgName}
          orgAddress={orgAddress}
          onBack={() => setShowClosedBills(false)}
        />
      );
    }

    if (showClosedJobs) {
      return (
        <ClosedJobsPage
          jobsRaw={jobsRaw}
          customers={customers}
          role={role}
          capabilities={capabilities}
          onOpenJob={(job) => {
            setShowClosedJobs(false);
            setSelectedJob(job);
          }}
          onBack={() => setShowClosedJobs(false)}
        />
      );
    }

    if (showDeletedItemsPage && isOrgAdmin) {
      return (
        <DeletedItemsPage
          customers={customers}
          actions={actions}
          user={user}
          globalAuditLog={globalAuditLog}
          onBack={() => setShowDeletedItemsPage(false)}
        />
      );
    }

    if (selectedContactLocation) {
      return (
        <ContactsPage
          customer={currentCustomer}
          location={selectedContactLocation}
          actions={actions}
          user={user}
          role={role}
          setSelectedContactLocation={setSelectedContactLocation}
        />
      );
    }

    if (selectedContactCustomer) {
      const liveCustomer =
        customers.find((c) => c.id === selectedContactCustomer.id) ||
        selectedContactCustomer;
      return (
        <ContactsPage
          customer={liveCustomer}
          actions={actions}
          user={user}
          role={role}
          setSelectedContactCustomer={setSelectedContactCustomer}
        />
      );
    }

    if (warrantiesForEquipment) {
      return (
        <WarrantiesPage
          customer={currentCustomer}
          location={selectedLocation}
          equipment={warrantiesForEquipment}
          actions={actions}
          role={role}
          capabilities={capabilities}
          onBack={() => setWarrantiesForEquipment(null)}
        />
      );
    }

    if (selectedEquipment) {
      const equipmentOnBack = pmListView
        ? () => {
            setSelectedEquipment(null);
            setSelectedLocation(null);
            setSelectedCustomer(null);
          }
        : undefined;
      return (
        <EquipmentDetailPage
          customer={currentCustomer}
          location={selectedLocation}
          equipment={selectedEquipment}
          actions={actions}
          user={user}
          role={role}
          orgId={orgId}
          orgType={orgType}
          capabilities={capabilities}
          setSelectedEquipment={setSelectedEquipment}
          setWarrantiesForEquipment={setWarrantiesForEquipment}
          onBack={equipmentOnBack}
        />
      );
    }

    // equipmentStatusView is checked BEFORE selectedLocation/Customer so a
    // click on the Caution/Disabled tiles inside LocationDetailPage actually
    // navigates to the filtered list instead of staying on the location
    // page (which kept rendering because selectedLocation is still set).
    // selectedEquipment above us still wins, so drilling from the status
    // list into an individual equipment record routes correctly. Back from
    // EquipmentByStatusPage clears equipmentStatusView and falls back to
    // selectedLocation (LocationDetailPage), so the user returns to where
    // they came from.
    if (equipmentStatusView) {
      return (
        <EquipmentByStatusPage
          status={equipmentStatusView}
          equipment={equipmentStatusList}
          customers={isFacility ? facilityCustomers : customers}
          setSelectedCustomer={setSelectedCustomer}
          setSelectedLocation={setSelectedLocation}
          setSelectedEquipment={setSelectedEquipment}
          setSelectedContactLocation={setSelectedContactLocation}
          onBack={() => {
            setEquipmentStatusView(null);
            setEquipmentStatusList([]);
          }}
        />
      );
    }

    if (selectedLocation) {
      return (
        <LocationDetailPage
          customer={currentCustomer}
          location={selectedLocation}
          actions={actions}
          role={role}
          capabilities={capabilities}
          orgId={orgId}
          setSelectedLocation={setSelectedLocation}
          setSelectedEquipment={setSelectedEquipment}
          onShowEquipmentByStatus={(status, list) => {
            setEquipmentStatusList(list || []);
            setEquipmentStatusView(status);
          }}
        />
      );
    }

    if (selectedCustomer) {
      return (
        <CustomerDetailPage
          customer={currentCustomer}
          contactsRaw={contactsRaw}
          actions={actions}
          role={role}
          capabilities={capabilities}
          user={user}
          setSelectedCustomer={setSelectedCustomer}
          setSelectedLocation={setSelectedLocation}
          setSelectedContactLocation={setSelectedContactLocation}
          setSelectedContactCustomer={setSelectedContactCustomer}
        />
      );
    }

    if (showCustomersPage) {
      return (
        <CustomersPage
          customers={customers}
          contactsRaw={contactsRaw}
          actions={actions}
          role={role}
          capabilities={capabilities}
          user={user}
          setSelectedCustomer={setSelectedCustomer}
          onBack={() => setShowCustomersPage(false)}
        />
      );
    }

    if (pmListView) {
      return (
        <PmListPage
          mode={pmListView}
          customers={isFacility ? facilityCustomers : customers}
          setSelectedCustomer={setSelectedCustomer}
          setSelectedLocation={setSelectedLocation}
          setSelectedEquipment={setSelectedEquipment}
          setSelectedContactLocation={setSelectedContactLocation}
          onBack={() => setPmListView(null)}
        />
      );
    }

    if (isFacility) {
      const canViewSignedTickets = hasCapability(
        role,
        capabilities,
        "viewSignedTickets"
      );
      return (
        <FacilityDashboard
          customers={facilityCustomers}
          jobsRaw={jobsRaw}
          publicJobsRaw={publicJobsRaw}
          publicTicketsRaw={publicTicketsRaw}
          publicInvoicesRaw={publicInvoicesRaw}
          messageThreadsRaw={messageThreadsRaw}
          publicQuotesRaw={publicQuotesRaw}
          notificationsRaw={notificationsRaw}
          dismissedNotificationIds={dismissedNotificationIds}
          orgId={orgId}
          uid={user?.uid}
          role={role}
          capabilities={capabilities}
          threadLastRead={threadLastRead}
          canViewSignedTickets={canViewSignedTickets}
          onShowBuildings={() => setShowFacilityBuildings(true)}
          onShowJobs={() => setShowFacilityJobs(true)}
          onShowRecentActivity={() => setShowRecentActivityPage(true)}
          onShowSignedTickets={() => setShowSignedTickets(true)}
          onShowMessages={() => setShowMessages(true)}
          onShowPmDueSoon={() => setPmListView("dueSoon")}
          onShowPmOverdue={() => setPmListView("overdue")}
          onShowQuotes={() => setShowFacilityQuotes(true)}
          onShowInvoices={() => setShowFacilityInvoices(true)}
          onShowEquipmentByStatus={(status, list) => {
            setEquipmentStatusList(list || []);
            setEquipmentStatusView(status);
          }}
          facilityServiceTicketsRaw={facilityServiceTicketsRaw}
          onShowFacilityTickets={() => setShowFacilityTickets(true)}
        />
      );
    }

    return (
      <DashboardPage
        customers={customers}
        setSelectedCustomer={setSelectedCustomer}
        setSelectedLocation={setSelectedLocation}
        setSelectedEquipment={setSelectedEquipment}
        setSelectedContactLocation={setSelectedContactLocation}
        messageThreadsRaw={messageThreadsRaw}
        notificationsRaw={notificationsRaw}
        dismissedNotificationIds={dismissedNotificationIds}
        orgId={orgId}
        uid={user?.uid}
        user={user}
        role={role}
        capabilities={capabilities}
        department={department}
        threadLastRead={threadLastRead}
        onShowCustomers={() => setShowCustomersPage(true)}
        onShowRecentActivity={() => setShowRecentActivityPage(true)}
        onShowJobs={() => setShowJobsPage(true)}
        onShowServiceTickets={() => setShowServiceTicketsPage(true)}
        onShowSignedTickets={() => {
          setServiceTicketsInitialStatus("signed");
          setShowServiceTicketsPage(true);
        }}
        onShowMessages={() => setShowMessages(true)}
        onShowQuotes={() => setShowQuotesPage(true)}
        onShowInvoices={
          hasCapability(role, capabilities, "viewInvoices")
            ? () => setShowInvoicesPage(true)
            : undefined
        }
        onShowContracts={() => setShowContractsPage(true)}
        onShowPOs={() => setShowPOsPage(true)}
        onShowVendors={
          hasCapability(role, capabilities, "manageVendors")
            ? () => setShowVendorsPage(true)
            : undefined
        }
        onShowMargin={
          hasCapability(role, capabilities, "viewMargin")
            ? () => setShowMarginPage(true)
            : undefined
        }
        vendorsRaw={vendorsRaw}
        jobsRaw={jobsRaw}
        serviceTicketsRaw={serviceTicketsRaw}
        quotesRaw={quotesRaw}
        purchaseOrdersRaw={purchaseOrdersRaw}
        contractsRaw={contractsRaw}
        timeEntriesRaw={timeEntriesRaw}
        onShowMyTime={() => setShowMyTime(true)}
        onShowClosedJobs={
          hasCapability(role, capabilities, "viewClosedJobs")
            ? () => setShowClosedJobs(true)
            : undefined
        }
        onOpenJob={(job) => setSelectedJob(job)}
        onShowPmDueSoon={() => setPmListView("dueSoon")}
        onShowPmOverdue={() => setPmListView("overdue")}
        onShowEquipmentByStatus={(status, list) => {
          setEquipmentStatusList(list || []);
          setEquipmentStatusView(status);
        }}
      />
    );
  };

  return (
    <>
      <BrandHeader
        left={
          <HeaderMenu
            items={[
              {
                label: "Home",
                onClick: goHome,
                hidden: isOnHome,
              },
              {
                label: "My Profile",
                onClick: () => {
                  goHome();
                  setShowProfile(true);
                },
                hidden: !user,
              },
              {
                label: "Connections",
                onClick: () => {
                  goHome();
                  setShowConnections(true);
                },
                hidden: !orgId || isNativeMobile(),
              },
              {
                label: "Work Records",
                onClick: () => {
                  goHome();
                  setShowFacilityWorkRecords(true);
                },
                hidden: !orgId || !isFacility,
              },
              {
                label: "My Time",
                onClick: () => {
                  goHome();
                  setShowMyTime(true);
                },
                hidden: !orgId,
              },
              {
                label: "Payroll",
                onClick: () => {
                  goHome();
                  setShowPayroll(true);
                },
                hidden:
                  !orgId ||
                  !hasCapability(role, capabilities, "approveTime") ||
                  isNativeMobile(),
              },
              {
                label: "Closed Jobs",
                onClick: () => {
                  goHome();
                  setShowClosedJobs(true);
                },
                // Dedicated viewClosedJobs cap so admin can grant it
                // independently of editJobs (billing preset turns
                // editJobs OFF on purpose, so the previous gate hid
                // the page from the exact audience it was built for).
                hidden:
                  !orgId ||
                  !hasCapability(role, capabilities, "viewClosedJobs") ||
                  isNativeMobile(),
              },
              {
                label: "Closed Bills",
                onClick: () => {
                  goHome();
                  setShowClosedBills(true);
                },
                // Dedicated viewClosedBills cap (default-on in Billing
                // dept). Earlier OR'd hack on viewPaymentHistory was the
                // wrong gate — Justin asked for a proper permission so
                // admins can grant access independently.
                hidden:
                  !orgId ||
                  !hasCapability(role, capabilities, "viewClosedBills") ||
                  isNativeMobile(),
              },
              {
                label: "Manage Employees",
                onClick: () => {
                  goHome();
                  setShowManageUsersPage(true);
                },
                hidden: !isOrgAdmin || isNativeMobile(),
              },
              {
                label: "Company Settings",
                onClick: () => {
                  goHome();
                  setShowCompanySettings(true);
                },
                hidden: !isOrgAdmin || isNativeMobile(),
              },
              {
                label: "Deleted Items",
                onClick: () => {
                  goHome();
                  setShowDeletedItemsPage(true);
                },
                hidden: !isOrgAdmin || isNativeMobile(),
              },
              {
                label: "Logout",
                onClick: handleLogout,
                danger: true,
              },
            ]}
          />
        }
        onHome={goHome}
        isOnHome={isOnHome}
        right={
          <NotificationsBell
            notifications={notificationsRaw}
            uid={user?.uid}
            role={role}
            notificationPreferences={notificationPreferences}
            dismissedNotificationIds={dismissedNotificationIds}
            onMarkRead={actions.markNotificationRead}
            onDismiss={actions.dismissNotifications}
            onNavigate={navigateToNotification}
            onReload={reconnectNotifications}
            onOpenSettings={() => {
              goHome();
              setShowNotificationSettings(true);
            }}
          />
        }
      />
      <div style={styles.page}>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          marginBottom: "4px",
        }}
      >
        {orgLogoUrl && (
          <img
            src={orgLogoUrl}
            alt={orgName ? `${orgName} logo` : "Company logo"}
            style={{
              height: "40px",
              maxWidth: "160px",
              objectFit: "contain",
            }}
          />
        )}
        <p style={{ fontSize: "12px", color: "#666", margin: 0 }}>
          <strong>{displayName(profile, user.email)}</strong>
          {orgName ? ` · ${orgName}` : ""}
          {role ? (
            <span style={{ textTransform: "capitalize" }}>
              {" · "}
              {role.replace(/_/g, " ")}
            </span>
          ) : null}
        </p>
      </div>
      <div style={{ marginBottom: "8px" }} />

      {!isOwner && !orgId && (
        <div
          style={{
            border: "1px solid #4a90e2",
            background: "#f0f8ff",
            padding: "12px",
            marginBottom: "12px",
            borderRadius: "6px",
          }}
        >
          <strong>Not attached to an organization</strong>
          <p style={{ fontSize: "12px", color: "#666", margin: "6px 0" }}>
            If you're the project owner (your email is on the bootstrap
            allowlist in the Cloud Function), click below to set your owner
            claim. Then log out and back in to refresh your session.
          </p>
          <button
            style={styles.button}
            onClick={handleBootstrap}
            disabled={bootstrapping}
          >
            {bootstrapping ? "Setting up..." : "Bootstrap Owner"}
          </button>
          {bootstrapMessage && (
            <p style={{ marginTop: "8px" }}>{bootstrapMessage}</p>
          )}
        </div>
      )}

      {errorMessage && (
        <div style={{ color: "red", marginBottom: 12 }}>
          Error: {errorMessage}
        </div>
      )}

      {!showProfile && (
        <ProfilePromptBanner
          profile={profile}
          onOpenProfile={() => {
            goHome();
            setShowProfile(true);
          }}
        />
      )}

      {renderPage()}
    </div>
    </>
  );
}
